import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ClientForm from "@/components/ClientForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, ArrowUpDown, Users, Star, Upload, Download, AlertTriangle, Archive, ArchiveRestore } from "lucide-react";

const statusStyles = {
  Lead: "bg-amber-100 text-amber-700",
  Active: "bg-emerald-100 text-emerald-700",
  Inactive: "bg-zinc-100 text-zinc-500",
};

const StatusPill = ({ status }) => (
  <span className={`rounded-full text-xs px-2.5 py-0.5 font-[Manrope] font-medium ${statusStyles[status]}`}>
    {status}
  </span>
);

const SUB_STARS = { None: 0, Standard: 2, Premium: 3 };
const gradeOf = (v) => (v >= 200000 ? "Gold" : v >= 50000 ? "Silver" : v > 0 ? "Bronze" : "—");
const gradeCls = { Gold: "bg-[#C99734] text-white", Silver: "bg-zinc-300 text-zinc-800", Bronze: "bg-amber-700 text-white", "—": "bg-zinc-100 text-zinc-500" };
const SOURCE_FILTER_OPTIONS = ["LinkedIn", "Polecenie", "Własne kręgi", "Konferencja/Targi", "Strona WWW", "Cold outreach (e-mail/tel.)", "Media społecznościowe", "Inne", "Nieznane"];
const CLIENT_FIELDS = [
  ["__skip__", "— pomiń —"],
  ["company_name", "Nazwa firmy *"],
  ["contact_person", "Osoba kontaktowa"],
  ["email", "E-mail"],
  ["phone", "Telefon"],
  ["region", "Region"],
  ["country", "Kraj"],
  ["status", "Status"],
  ["subscription", "Abonament"],
  ["annual_value", "Wartość roczna"],
  ["address", "Adres"],
  ["nip", "NIP"],
  ["source", "Źródło"],
  ["regon", "REGON"],
  ["krs", "KRS"],
  ["bank_account", "Numer konta"],
  ["notes", "Notatki"],
];

export default function Clients() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [subFilter, setSubFilter] = useState("all");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "all");
  const [countryFilter, setCountryFilter] = useState(searchParams.get("country") || "all");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("all");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archivedView, setArchivedView] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/clients", {
        params: { search, region, status, sort_by: sortBy, sort_dir: sortDir, archived: archivedView },
      });
      setClients(res.data);
    } catch (e) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [search, region, status, sortBy, sortDir, archivedView]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir("asc"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/clients/${deleteTarget.client_id}`);
      toast.success("Klient zarchiwizowany");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Nie udało się zarchiwizować klienta");
    }
  };

  const restoreClient = async (c) => {
    try {
      await api.post(`/clients/${c.client_id}/restore`);
      toast.success(`Przywrócono: ${c.company_name}`);
      load();
    } catch (e) {
      toast.error("Nie udało się przywrócić klienta");
    }
  };

  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [enrich, setEnrich] = useState(true);
  const [dupAction, setDupAction] = useState("skip");

  const downloadTemplate = () => {
    const headers = ["nazwa firmy", "osoba kontaktowa", "email", "telefon", "region", "kraj", "status", "abonament", "wartość roczna", "adres", "nip", "źródło", "regon", "krs", "numer konta", "notatki"];
    const example = ["Przykładowa Sp. z o.o.", "Jan Kowalski", "jan@example.pl", "+48 123 456 789", "Poland", "Polska", "Lead", "Standard", "50000", "ul. Przykładowa 1, 00-001 Warszawa", "1234563218", "LinkedIn", "", "", "", ""];
    const csv = "\ufeff" + headers.join(";") + "\n" + example.join(";") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "szablon_klienci.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/clients/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const m = {};
      (data.headers || []).forEach((h) => { m[h] = data.auto_mapping?.[h] || "__skip__"; });
      setImportFile(f);
      setPreview(data);
      setMapping(m);
      setImportOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się wczytać pliku");
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const mp = {};
      Object.entries(mapping).forEach(([h, f]) => { if (f && f !== "__skip__") mp[h] = f; });
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("mapping", JSON.stringify(mp));
      fd.append("enrich", enrich ? "true" : "false");
      fd.append("duplicate_action", dupAction);
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Zaimportowano ${data.created}${data.updated ? `, zaktualizowano ${data.updated}` : ""}${data.skipped ? `, pominięto ${data.skipped}` : ""}`);
      setImportOpen(false);
      setImportFile(null);
      setPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import nie powiódł się");
    } finally {
      setImporting(false);
    }
  };

  const SortHead = ({ field, label }) => (
    <TableHead>
      <button
        onClick={() => toggleSort(field)}
        data-testid={`sort-${field}`}
        className="flex items-center gap-1 font-[Manrope] hover:text-zinc-900 transition-colors"
      >
        {label}
        <ArrowUpDown className={`h-3.5 w-3.5 ${sortBy === field ? "text-[#193962]" : "text-zinc-400"}`} />
      </button>
    </TableHead>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-[Outfit] font-bold tracking-tight text-4xl sm:text-5xl">Clients</h1>
            <p className="text-base text-muted-foreground font-[Manrope] mt-2">
              {clients.length} client{clients.length !== 1 ? "s" : ""} across all regions.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid="import-csv-input" />
            <Button
              onClick={downloadTemplate}
              variant="ghost"
              data-testid="download-template-btn"
              className="rounded-md text-[#193962] hover:bg-[#193962]/5"
            >
              <Download className="h-4 w-4 mr-2" /> Szablon CSV
            </Button>
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              variant="outline"
              data-testid="import-csv-btn"
              className="rounded-md transition-all duration-200 hover:-translate-y-[1px]"
            >
              <Upload className="h-4 w-4 mr-2" /> {importing ? "Importuję…" : "Import CSV"}
            </Button>
            <Button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              data-testid="add-client-btn"
              className="bg-[#193962] hover:bg-[#12294A] text-white rounded-md transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md"
            >
              <Plus className="h-4 w-4 mr-2" /> New Client
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="client-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company, contact, email…"
              className="pl-9 bg-white"
            />
          </div>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger data-testid="filter-region" className="w-40 bg-white">
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="Poland">Poland</SelectItem>
              <SelectItem value="Kenya">Kenya</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="w-40 bg-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Lead">Lead</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={gradeFilter} onValueChange={setGradeFilter}>
            <SelectTrigger data-testid="filter-grade" className="w-36 bg-white"><SelectValue placeholder="Dochodowość" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Dochodowość</SelectItem>
              <SelectItem value="Gold">Gold</SelectItem>
              <SelectItem value="Silver">Silver</SelectItem>
              <SelectItem value="Bronze">Bronze</SelectItem>
            </SelectContent>
          </Select>
          <Select value={subFilter} onValueChange={setSubFilter}>
            <SelectTrigger data-testid="filter-subscription" className="w-36 bg-white"><SelectValue placeholder="Abonament" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Abonament</SelectItem>
              <SelectItem value="Premium">Premium</SelectItem>
              <SelectItem value="Standard">Standard</SelectItem>
              <SelectItem value="None">Brak</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="filter-source" className="w-44 bg-white"><SelectValue placeholder="Źródło" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Źródło kontaktu</SelectItem>
              {SOURCE_FILTER_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger data-testid="filter-country" className="w-40 bg-white"><SelectValue placeholder="Kraj" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Wszystkie kraje</SelectItem>
              {[...new Set(clients.map((c) => (c.country || "").trim()).filter(Boolean))].sort().map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={archivedView ? "default" : "outline"}
            onClick={() => setArchivedView((v) => !v)}
            data-testid="toggle-archived-btn"
            className={archivedView ? "bg-[#193962] hover:bg-[#12294A] text-white" : ""}
          >
            <Archive className="h-4 w-4 mr-2" /> {archivedView ? "Pokaż aktywnych" : "Archiwum"}
          </Button>
        </div>

        {/* Table */}
        <div className="mt-6 border border-border bg-white rounded-md overflow-x-auto" data-testid="clients-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <SortHead field="company_name" label="Company" />
                <TableHead className="font-[Manrope]">Contact</TableHead>
                <TableHead className="font-[Manrope]">Email</TableHead>
                <TableHead className="font-[Manrope]">Phone</TableHead>
                <SortHead field="region" label="Region" />
                <SortHead field="status" label="Status" />
                <TableHead className="font-[Manrope]">Abonament</TableHead>
                <TableHead className="font-[Manrope]">Dochodowość</TableHead>
                <TableHead className="font-[Manrope] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground font-[Manrope]">Loading…</TableCell></TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16">
                    <Users className="h-8 w-8 mx-auto text-zinc-300 mb-3" />
                    <p className="font-[Manrope] text-muted-foreground">No clients match your filters.</p>
                  </TableCell>
                </TableRow>
              ) : (
                clients.filter((c) => (subFilter === "all" || c.subscription === subFilter) && (gradeFilter === "all" || gradeOf(c.annual_value || 0) === gradeFilter) && (sourceFilter === "all" || ((c.source || "").trim() || "Nieznane") === sourceFilter) && (countryFilter === "all" || (c.country || "").trim() === countryFilter)).map((c) => (
                  <TableRow key={c.client_id} data-testid={`client-row-${c.client_id}`} className="group">
                    <TableCell className="font-[Manrope] font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/clients/${c.client_id}`)}
                          data-testid={`view-client-${c.client_id}`}
                          className="hover:text-[#193962] hover:underline transition-colors text-left"
                        >
                          {c.company_name}
                        </button>
                        {c.vat_status && c.vat_status !== "Czynny" && (
                          <span title={`Status VAT: ${c.vat_status}`} data-testid={`vat-warning-${c.client_id}`} className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 text-[10px] px-2 py-0.5 font-medium">
                            <AlertTriangle className="h-3 w-3" /> VAT
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-[Manrope] text-sm">{c.contact_person || "—"}</TableCell>
                    <TableCell className="font-[Manrope] text-sm text-muted-foreground">{c.email || "—"}</TableCell>
                    <TableCell className="font-[Manrope] text-sm text-muted-foreground">{c.phone || "—"}</TableCell>
                    <TableCell className="font-[Manrope] text-sm">{c.region}{c.country ? <span className="block text-xs text-zinc-400">{c.country}</span> : null}</TableCell>
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell>
                      {SUB_STARS[c.subscription] > 0 ? (
                        <span className="flex items-center gap-0.5">{Array.from({ length: SUB_STARS[c.subscription] }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 text-[#C99734] fill-[#C99734]" />)}</span>
                      ) : <span className="text-xs text-zinc-400 font-[Manrope]">—</span>}
                    </TableCell>
                    <TableCell><span className={`rounded-full text-xs px-2 py-0.5 font-[Manrope] font-medium ${gradeCls[gradeOf(c.annual_value || 0)]}`}>{gradeOf(c.annual_value || 0)}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setEditing(c); setFormOpen(true); }}
                          data-testid={`edit-client-${c.client_id}`}
                          className="h-8 w-8 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-500 hover:text-[#193962] transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {archivedView ? (
                          <button
                            onClick={() => restoreClient(c)}
                            data-testid={`restore-client-${c.client_id}`}
                            className="h-8 w-8 flex items-center justify-center rounded hover:bg-emerald-50 text-zinc-500 hover:text-emerald-600 transition-colors"
                            title="Przywróć klienta"
                          >
                            <ArchiveRestore className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setDeleteTarget(c)}
                            data-testid={`delete-client-${c.client_id}`}
                            className="h-8 w-8 flex items-center justify-center rounded hover:bg-amber-50 text-zinc-500 hover:text-amber-600 transition-colors"
                            title="Archiwizuj klienta"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportFile(null); setPreview(null); } }}>
        <DialogContent className="bg-white max-w-4xl" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] text-2xl tracking-tight">Import CSV — podgląd</DialogTitle>
            <DialogDescription className="font-[Manrope]">
              {preview ? `Wykryto ${preview.total_rows} wierszy. Dopasuj kolumny i potwierdź import.` : ""}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
              <label className="flex items-center gap-2 text-sm font-[Manrope] text-zinc-700 cursor-pointer" data-testid="enrich-toggle">
                <input type="checkbox" checked={enrich} onChange={(e) => setEnrich(e.target.checked)} className="h-4 w-4 accent-[#193962]" />
                Uzupełnij dane z Białej Listy MF (nazwa/adres/konto + status VAT) dla wierszy z NIP-em
              </label>
              <div className="flex items-center gap-2 text-sm font-[Manrope] text-zinc-700" data-testid="dup-action-row">
                <span>Gdy duplikat (NIP/e-mail):</span>
                <Select value={dupAction} onValueChange={setDupAction}>
                  <SelectTrigger className="w-48 bg-white h-9" data-testid="dup-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="skip">Pomiń</SelectItem>
                    <SelectItem value="update">Zaktualizuj (uzupełnij braki)</SelectItem>
                    <SelectItem value="create">Utwórz mimo to</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {preview.duplicate_count > 0 && (
                <p className="text-xs text-amber-700 font-[Manrope] bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="duplicate-summary">
                  ⚠ Wykryto {preview.duplicate_count} możliwych duplikatów (dopasowanie po NIP/e-mailu).
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {preview.headers.map((h) => (
                  <div key={h} className="flex items-center gap-2" data-testid={`map-row-${h}`}>
                    <span className="w-36 text-sm font-[Manrope] text-zinc-700 truncate shrink-0" title={h}>{h}</span>
                    <span className="text-zinc-400">→</span>
                    <Select value={mapping[h] || "__skip__"} onValueChange={(v) => setMapping((m) => ({ ...m, [h]: v }))}>
                      <SelectTrigger className="flex-1 bg-white h-9" data-testid={`map-select-${h}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {CLIENT_FIELDS.map(([val, label]) => <SelectItem key={val} value={val}>{label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="border border-border rounded-md overflow-x-auto">
                <table className="w-full text-sm font-[Manrope]">
                  <thead className="bg-zinc-50">
                    <tr><th className="text-left px-3 py-2 font-medium whitespace-nowrap">Status</th>{preview.headers.map((h) => <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row, ri) => (
                      <tr key={ri} className="border-t border-border" data-testid={`preview-row-${ri}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          {preview.dup_flags?.[ri] ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 text-[10px] px-2 py-0.5 font-medium" data-testid={`dup-badge-${ri}`}>duplikat</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 font-medium">nowy</span>
                          )}
                        </td>
                        {preview.headers.map((h) => <td key={h} className="px-3 py-1.5 text-zinc-600 whitespace-nowrap max-w-[200px] truncate" title={row[h]}>{row[h] || "—"}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} data-testid="import-cancel-btn">Anuluj</Button>
            <Button onClick={confirmImport} disabled={importing} data-testid="import-confirm-btn" className="bg-[#193962] hover:bg-[#12294A] text-white">
              {importing ? "Importuję…" : `Importuj ${preview?.total_rows || 0} wierszy`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClientForm open={formOpen} onOpenChange={setFormOpen} client={editing} onSaved={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-[Outfit]">Zarchiwizować klienta?</AlertDialogTitle>
            <AlertDialogDescription className="font-[Manrope]">
              <strong>{deleteTarget?.company_name}</strong> zostanie przeniesiony do archiwum. Powiązane spotkania i synergie pozostaną zachowane, a klienta można w każdej chwili przywrócić z widoku „Archiwum".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm-btn" className="bg-amber-600 hover:bg-amber-700">
              Archiwizuj
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
